import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Users, Clock, Timer, CalendarDays } from "lucide-react";
import { Services } from "../../../utilities/models/ServiceModel";
import {
  isTimeBetweenBoundaries,
  isDateInTheFuture,
  computeDurationInHours,
  ceilToHalfHour,
} from "../../../utilities/functions/Helpers";
import FacilityConstants from "../../../utilities/constants/FacilityConstants";

const StepBasics = ({
  onNext,
  onPrev,
  initialData,
  rates,
  availabilityState,
  onAvailabilityCheck,
}) => {
  const [basics, setBasics] = useState({
    startDate: initialData.startDate || "",
    endDate: initialData.endDate || "",
    guests: initialData.guests || "",
    eventType: initialData.eventType || "",
    pricingStrategy: initialData.pricingStrategy || "FULL_DAY",
  });
  const [errors, setErrors] = useState({});
  const lastAutoCheckedRangeRef = useRef(null);

  const currentRangeKey = useMemo(() => {
    if (!basics.startDate || !basics.endDate) {
      return null;
    }

    return `${basics.startDate}|${basics.endDate}`;
  }, [basics.endDate, basics.startDate]);

  const localDateValidation = useMemo(() => {
    if (initialData.type !== "reservation") {
      return { isReady: false, message: "" };
    }

    if (!basics.startDate || !basics.endDate) {
      return { isReady: false, message: "" };
    }

    if (basics.endDate <= basics.startDate) {
      return { isReady: false, message: "End date must be after start date" };
    }

    return { isReady: true, message: "" };
  }, [basics.endDate, basics.startDate, initialData.type]);

  useEffect(() => {
    if (initialData.type !== "reservation" || !onAvailabilityCheck) {
      return;
    }

    if (!localDateValidation.isReady) {
      lastAutoCheckedRangeRef.current = null;
      onAvailabilityCheck(null);
      return;
    }

    if (lastAutoCheckedRangeRef.current === currentRangeKey) {
      return;
    }

    lastAutoCheckedRangeRef.current = currentRangeKey;

    onAvailabilityCheck({
      startDate: basics.startDate,
      endDate: basics.endDate,
    });
  }, [
    basics.endDate,
    basics.startDate,
    currentRangeKey,
    initialData.type,
    localDateValidation.isReady,
    onAvailabilityCheck,
  ]);

  // Min date helper (same as original form)
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const validate = () => {
    const newErrors = {};

    // Date & Time Validation
    if (!basics.startDate) {
      newErrors.startDate = "Start date is required";
    } else {
      const start = new Date(basics.startDate);
      if (!isDateInTheFuture(basics.startDate)) {
        newErrors.startDate = "Date must be in the future";
      } else if (!isTimeBetweenBoundaries(start)) {
        newErrors.startDate = "Time must be between 9:00 AM and 7:30 PM";
      }
    }

    if (initialData.type === "reservation") {
      if (!basics.endDate) {
        newErrors.endDate = "End date is required";
      } else {
        if (basics.endDate < basics.startDate) {
          newErrors.endDate = "End date must be after start date";
        }
        // Note: End time might go late (events can end late), but if rule applies to appointments mainly:
        // If it's a reservation, usually end time matters for overtime logic, but strict business hours might not apply same way?
        // The prompt says "appointments can be accepted... restriction or time intervals".
        // I will apply strict 9-7:30 only if it's an appointment OR check existing Reservation rules.
        // ReservationModel has: isTimeBetweenBoundaries(value, { hours: 9, minutes: 0 }, { hours: 22, minutes: 0 }) -> 9 AM to 10 PM!
        // AppointmentForm has 9 AM to 7:30 PM.

        // Let's distinguish.
      }

      // Re-validate start for Reservation specific hours if needed?
      // ReservationModel says 9AM-10PM. Appointment says 9AM-7:30PM.
      // Let's refine the boundary check based on type.
      const start = new Date(basics.startDate);
      const lower = { hours: 9, minutes: 0 };
      const upper =
        initialData.type === "reservation"
          ? { hours: 22, minutes: 0 }
          : { hours: 19, minutes: 30 };

      if (basics.startDate && !isTimeBetweenBoundaries(start, lower, upper)) {
        newErrors.startDate =
          initialData.type === "reservation"
            ? "Time must be between 9:00 AM and 10:00 PM"
            : "Time must be between 9:00 AM and 7:30 PM";
      }

      // For reservation end date, usually 10PM is regular, after is overtime?
      // ReservationModel doesn't validate end date boundaries strictly, just that it's after start.
      // But let's stick to the prompt "appointments... restriction".
    } else {
      // Appointment specific strict check (already done generally above, but let's be precise)
      // The general check above used default isTimeBetweenBoundaries which is 9-7:30.
      // So if it's appointment, it's covered.
      // If reservation, we need the wider 9-10pm window.
    }

    // Let's rewrite the logic cleanly:

    if (basics.startDate) {
      const start = new Date(basics.startDate);
      if (!isDateInTheFuture(basics.startDate)) {
        newErrors.startDate = "Date must be in the future";
      } else {
        const lower = { hours: 9, minutes: 0 };
        const upper =
          initialData.type === "reservation"
            ? { hours: 22, minutes: 0 }
            : { hours: 19, minutes: 30 };

        if (!isTimeBetweenBoundaries(start, lower, upper)) {
          newErrors.startDate =
            initialData.type === "reservation"
              ? "Time must be between 9:00 AM and 10:00 PM"
              : "Time must be between 9:00 AM and 7:30 PM";
        }
      }
    }

    if (initialData.type === "reservation" && !basics.endDate)
      newErrors.endDate = "End date is required";
    if (
      initialData.type === "reservation" &&
      basics.endDate &&
      basics.startDate &&
      basics.endDate <= basics.startDate
    )
      newErrors.endDate = "End date must be after start date";

    if (!basics.guests || basics.guests <= 0)
      newErrors.guests = "Guest count is required";
    if (basics.guests > 200) newErrors.guests = "Guest count cannot exceed 200";

    if (initialData.type === "reservation" && !basics.eventType)
      newErrors.eventType = "Event type is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      validate() &&
      !availabilityState?.isChecking &&
      availabilityState?.available !== false
    ) {
      onNext(basics);
    }
  };

  // Services list from model
  const serviceOptions = Services.filter((s) => s.available)
    .map((s) => s.title)
    .sort();

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-6 font-alata">
        {initialData.type === "reservation"
          ? "Event Details"
          : "Appointment Details"}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-lg mx-auto">
        {/* Event Type (Only for Reservation) */}
        {initialData.type === "reservation" && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Event Type
            </label>
            <select
              value={basics.eventType}
              onChange={(e) =>
                setBasics({ ...basics, eventType: e.target.value })
              }
              className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
            >
              <option value="">Select event type...</option>
              {serviceOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {errors.eventType && (
              <p className="text-red-500 text-sm">{errors.eventType}</p>
            )}
          </div>
        )}

        {/* Date & Time Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          <div className="space-y-2 w-full">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {initialData.type === "reservation"
                ? "Start Date & Time"
                : "Preferred Date & Time"}
            </label>
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Clock className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="datetime-local"
                min={getMinDateTime()}
                value={basics.startDate}
                onChange={(e) =>
                  setBasics({ ...basics, startDate: e.target.value })
                }
                className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:calendar-invert dark:text-white"
              />
            </div>
            {errors.startDate && (
              <p className="text-red-500 text-sm">{errors.startDate}</p>
            )}
          </div>

          {initialData.type === "reservation" && (
            <div className="space-y-2 w-full">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                End Date & Time
              </label>
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Clock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type="datetime-local"
                  min={basics.startDate || getMinDateTime()}
                  value={basics.endDate}
                  onChange={(e) =>
                    setBasics({ ...basics, endDate: e.target.value })
                  }
                  className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:calendar-invert dark:text-white"
                />
              </div>
              {errors.endDate && (
                <p className="text-red-500 text-sm">{errors.endDate}</p>
              )}
            </div>
          )}
        </div>

        {initialData.type === "reservation" &&
          (availabilityState?.isChecking || availabilityState?.available === false) && (
            <div className="space-y-2">
              {availabilityState?.isChecking && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                  Checking whether this time frame is still available...
                </div>
              )}

              {!availabilityState?.isChecking && availabilityState?.available === false && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
                  {availabilityState?.message || "That time frame is no longer available."}
                </div>
              )}
            </div>
          )}

        {/* Pricing Strategy Selector (Reservation Only) */}
        {initialData.type === "reservation" &&
          basics.startDate &&
          basics.endDate && (() => {
            const durationHours = computeDurationInHours(basics.startDate, basics.endDate);
            const bookedHours = ceilToHalfHour(durationHours);
            const hourlyRate = rates?.hourlyFacilityRate ?? FacilityConstants.HOURLY_FACILITY_RATE;
            const hourlyEstimate = bookedHours * hourlyRate;

            const startDay = new Date(basics.startDate);
            const dayName = FacilityConstants.WEEK_DAYS[startDay.getDay()];
            const isSaturday = dayName === FacilityConstants.WEEK_DAYS[FacilityConstants.WEEK_DAYS.length - 1];
            const fullDayEstimate = isSaturday
              ? (rates?.saturdayFacilityRate ?? FacilityConstants.SATURDAY_RATE)
              : (rates?.regularFacilityRate ?? FacilityConstants.REGULAR_DAYS_RATE);

            const hourlyBetter = hourlyEstimate < fullDayEstimate;
            const savings = Math.abs(fullDayEstimate - hourlyEstimate);
            const hourlyDisabled = durationHours > FacilityConstants.HOURLY_MAX_HOURS;

            return (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Pricing Plan
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Hourly Option */}
                  <button
                    type="button"
                    disabled={hourlyDisabled}
                    onClick={() => setBasics({ ...basics, pricingStrategy: "HOURLY" })}
                    className={`relative flex flex-col items-center p-4 rounded-xl border-2 transition-all text-left ${
                      hourlyDisabled
                        ? "opacity-50 cursor-not-allowed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
                        : basics.pricingStrategy === "HOURLY"
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md"
                          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300"
                    }`}
                  >
                    {!hourlyDisabled && hourlyBetter && (
                      <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        Best Value
                      </span>
                    )}
                    <Timer className="h-6 w-6 text-blue-500 mb-2" />
                    <span className="font-bold text-slate-900 dark:text-white text-sm">Hourly Rate</span>
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">
                      ${hourlyRate}/h
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 text-center mt-1">
                      {bookedHours}h × ${hourlyRate} = ${hourlyEstimate.toLocaleString()}
                    </span>
                    {hourlyDisabled && (
                      <span className="text-xs text-orange-500 mt-1">Max {FacilityConstants.HOURLY_MAX_HOURS}h</span>
                    )}
                  </button>

                  {/* Full-Day Option */}
                  <button
                    type="button"
                    onClick={() => setBasics({ ...basics, pricingStrategy: "FULL_DAY" })}
                    className={`relative flex flex-col items-center p-4 rounded-xl border-2 transition-all text-left ${
                      basics.pricingStrategy === "FULL_DAY"
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md"
                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300"
                    }`}
                  >
                    {!hourlyDisabled && !hourlyBetter && (
                      <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        Best Value
                      </span>
                    )}
                    {hourlyDisabled && (
                      <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        Recommended
                      </span>
                    )}
                    <CalendarDays className="h-6 w-6 text-blue-500 mb-2" />
                    <span className="font-bold text-slate-900 dark:text-white text-sm">Full-Day Rate</span>
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">
                      ${fullDayEstimate.toLocaleString()}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 text-center mt-1">
                      {isSaturday ? "Saturday" : "Sun–Fri"} flat rate
                    </span>
                  </button>
                </div>
                {!hourlyDisabled && savings > 0 && (
                  <p className="text-sm text-green-600 dark:text-green-400 text-center">
                    💡 {hourlyBetter ? "Hourly" : "Full-Day"} saves you ${savings.toLocaleString()} on facility fees!
                  </p>
                )}
              </div>
            );
          })()}

        {/* Guest Count */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {initialData.type === "reservation"
              ? "Number of Guests"
              : "Estimated Attendees"}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Users className="h-5 w-5 text-slate-400" />
            </div>
            <input
              type="number"
              min="1"
              max="200"
              value={basics.guests}
              onChange={(e) =>
                setBasics({
                  ...basics,
                  guests: Number.parseInt(e.target.value) || "",
                })
              }
              className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
              placeholder="Max 200 guests"
            />
          </div>
          {errors.guests && (
            <p className="text-red-500 text-sm">{errors.guests}</p>
          )}
        </div>

        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onPrev}
            className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={
              availabilityState?.isChecking ||
              availabilityState?.available === false
            }
            className={`px-8 py-2 text-white rounded-lg font-medium shadow-lg transition-all ${
              availabilityState?.isChecking || availabilityState?.available === false
                ? "bg-slate-400 cursor-not-allowed shadow-none"
                : "bg-blue-600 hover:bg-blue-700 hover:shadow-xl transform hover:-translate-y-0.5"
            }`}
          >
            {availabilityState?.isChecking ? "Checking availability..." : "Next Step"}
          </button>
        </div>
      </form>
    </div>
  );
};

StepBasics.propTypes = {
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    type: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    guests: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    eventType: PropTypes.string,
    pricingStrategy: PropTypes.string,
  }).isRequired,
  rates: PropTypes.shape({
    hourlyFacilityRate: PropTypes.number,
    saturdayFacilityRate: PropTypes.number,
    regularFacilityRate: PropTypes.number,
  }),
  availabilityState: PropTypes.shape({
    isChecking: PropTypes.bool,
    available: PropTypes.bool,
    message: PropTypes.string,
  }),
  onAvailabilityCheck: PropTypes.func,
};

export default StepBasics;
